function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
// inspired by chakra-ui, MIT https://github.com/chakra-ui/chakra-ui/blob/main/packages/components/transition/src/collapse.tsx
import React from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { sx } from '../..';
import { TransitionEasings, withDelay, mergeWith } from './collapse-utils';
const isNumeric = value => value != null && parseInt(value.toString(), 10) > 0;
const defaultTransitions = {
  exit: {
    height: {
      duration: 0.2,
      ease: TransitionEasings.ease
    },
    opacity: {
      duration: 0.3,
      ease: TransitionEasings.ease
    }
  },
  enter: {
    height: {
      duration: 0.3,
      ease: TransitionEasings.ease
    },
    opacity: {
      duration: 0.4,
      ease: TransitionEasings.ease
    }
  }
};
const variants = {
  exit: ({
    animateOpacity,
    startingHeight,
    transition,
    transitionEnd,
    delay
  }) => {
    var _transition$exit;
    return {
      ...(animateOpacity && {
        opacity: isNumeric(startingHeight) ? 1 : 0
      }),
      overflow: 'hidden',
      height: startingHeight,
      transitionEnd: transitionEnd === null || transitionEnd === void 0 ? void 0 : transitionEnd.exit,
      transition: (_transition$exit = transition === null || transition === void 0 ? void 0 : transition.exit) !== null && _transition$exit !== void 0 ? _transition$exit : withDelay.exit(defaultTransitions.exit, delay)
    };
  },
  enter: ({
    animateOpacity,
    endingHeight,
    transition,
    transitionEnd,
    delay
  }) => {
    var _transition$enter;
    return {
      ...(animateOpacity && {
        opacity: 1
      }),
      height: endingHeight,
      transitionEnd: transitionEnd === null || transitionEnd === void 0 ? void 0 : transitionEnd.enter,
      transition: (_transition$enter = transition === null || transition === void 0 ? void 0 : transition.enter) !== null && _transition$enter !== void 0 ? _transition$enter : withDelay.enter(defaultTransitions.enter, delay)
    };
  }
};
/**
 *
 * Creates regions of content that can expand/collapse with a simple animation.
 *
 * It helps to hide content that's not immediately relevant to the user.
 *
 */
const Collapse = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    in: isOpen,
    unmountOnExit,
    animateOpacity = true,
    startingHeight = 0,
    endingHeight = 'auto',
    style,
    UNSAFE_className,
    transition,
    transitionEnd,
    ...rest
  } = props;
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setMounted(true);
    });
    return () => clearTimeout(timeout);
  }, []);

  /**
   * Warn 🚨: `startingHeight` and `unmountOnExit` are mutually exclusive
   *
   * If you specify a starting height, the collapsed element needs to be mounted
   * for the height to take effect.
   */
  if (startingHeight > 0 && unmountOnExit) {
    console.warn(`startingHeight and unmountOnExit are mutually exclusive. You can't use them together`);
  }
  const hasStartingHeight = parseFloat(startingHeight.toString()) > 0;
  const custom = {
    startingHeight,
    endingHeight,
    animateOpacity,
    transition: !mounted ? {
      enter: {
        duration: 0
      }
    } : transition,
    transitionEnd: mergeWith(transitionEnd, {
      enter: {
        overflow: 'initial'
      },
      exit: unmountOnExit ? undefined : {
        display: hasStartingHeight ? 'block' : 'none'
      }
    })
  };
  const show = unmountOnExit ? isOpen : true;
  const animate = isOpen || unmountOnExit ? 'enter' : 'exit';
  return /*#__PURE__*/React.createElement(AnimatePresence, {
    initial: false,
    custom: custom
  }, show && /*#__PURE__*/React.createElement(StyledMotionDiv, _extends({
    ref: ref
  }, rest, {
    className: UNSAFE_className,
    style: {
      overflow: 'hidden',
      display: 'block',
      ...style
    },
    custom: custom,
    variants: variants,
    initial: unmountOnExit ? 'exit' : false,
    animate: animate,
    exit: "exit"
  })));
});
const StyledMotionDiv = styled(motion.div).withConfig({
  displayName: "Collapse__StyledMotionDiv",
  componentId: "sc-89zkp9-0"
})(["", ""], sx);
Collapse.displayName = 'Collapse';
export default Collapse;